import { configureDataTablesErrorMode } from './jqueryDatatablesShim';

export interface DataTableConfig {
  language: string;
  isAdmin: boolean;
  hasData: boolean;
  adminViewAll?: boolean;
}

export const shouldRebuildDataTable = (
  previous: DataTableConfig | null,
  current: DataTableConfig
): boolean => {
  if (!previous) {
    return true;
  }

  if (!previous.hasData && current.hasData) {
    return true;
  }

  if (previous.hasData !== current.hasData) {
    return true;
  }

  if (previous.language !== current.language) {
    return true;
  }

  if (previous.isAdmin !== current.isAdmin) {
    return true;
  }

  if (previous.adminViewAll !== current.adminViewAll) {
    return true;
  }

  return false;
};

/**
 * DataTables用の統一されたボタン設定を生成する
 * 順序: 表示・非表示、CSV、Excel、PDF、印刷
 */
export interface DataTableButtonOptions {
  title?: string;
  filename?: string | (() => string);
  pdfAction?: (e: unknown, dt: unknown, node: unknown, config: unknown) => void | Promise<void>;
  colvisColumns?: string;
  colvisShowAll?: boolean;
  t?: (key: string, options?: { defaultValue?: string }) => string;
}

export const getStandardDataTableButtons = (options: DataTableButtonOptions = {}) => {
  const {
    title,
    filename,
    pdfAction,
    colvisColumns = ':not(.noVis)',
    colvisShowAll = true,
  } = options;

  const buttons: Record<string, unknown>[] = [
    {
      extend: 'colvis',
      text: '表示・非表示',
      className: 'btn btn-secondary btn-sm',
      columns: colvisColumns,
      showAll: colvisShowAll,
      postfixButtons: colvisShowAll ? ['colvisRestore'] : undefined,
    },
    {
      extend: 'csv',
      text: 'CSV',
      className: 'buttons-csv',
      charset: 'utf-8',
      bom: true,
      filename: filename,
      exportOptions: {
        columns: ':visible',
      },
    },
    {
      extend: 'excel',
      text: 'Excel',
      className: 'buttons-excel',
      title: title,
      filename: filename,
      exportOptions: {
        columns: ':visible',
      },
    },
  ];

  // PDFボタン（カスタムアクションがある場合のみ追加）
  if (pdfAction) {
    buttons.push({
      text: 'PDF',
      className: 'buttons-pdf',
      action: pdfAction,
    });
  }

  // 印刷ボタン
  buttons.push({
    extend: 'print',
    text: '印刷',
    className: 'buttons-print',
    exportOptions: {
      columns: ':visible',
    },
  });

  return buttons;
};

/** DataTables 1.x で予約一覧テーブルが実際に呼ぶ最小 API（完全な Api 型は未導入） */
export interface ReservationDataTableApi {
  clear(): void;
  rows: { add: (rows: unknown[]) => void };
  draw(resetPaging?: boolean): void;
  destroy(remove?: boolean): void;
}

export interface ReservationDataTablesConstructor {
  (options: Record<string, unknown>): ReservationDataTableApi;
  isDataTable(el: HTMLElement): boolean;
}

export interface ReservationJQueryFnStatic {
  DataTable?: ReservationDataTablesConstructor;
  dataTable?: {
    ext?: {
      search?: unknown[];
    };
  };
}

export type ReservationJQueryElement = {
  DataTable: ReservationDataTablesConstructor;
  on: (events: string, selector: string, handler: (this: HTMLElement) => void) => unknown;
  data: (key: string) => unknown;
};

export type ReservationJQueryCallable = ((sel: string | HTMLElement) => ReservationJQueryElement) & {
  fn: ReservationJQueryFnStatic;
};

export function getReservationJQuery(): ReservationJQueryCallable | undefined {
  if (typeof window === 'undefined') return undefined;
  configureDataTablesErrorMode();
  return (window as unknown as { jQuery?: ReservationJQueryCallable }).jQuery;
}

/**
 * Destroys an existing DataTables instance if present, keeping the <table> in the DOM.
 * Use destroy(false) — destroy(true) / removing `.dataTables_wrapper` detaches React-owned
 * tables and breaks SPA remounts (empty page with "total: N" and no table).
 * Call before (re)initializing to avoid "Cannot reinitialise DataTable" (https://datatables.net/tn/3).
 */
export function destroyDataTableIfPresent(tableEl: HTMLElement | null): void {
  if (!tableEl || typeof window === 'undefined') return;
  const $ = (window as Window & { jQuery?: any }).jQuery;
  if (!$?.fn?.DataTable) return;
  if (!$.fn.DataTable.isDataTable?.(tableEl)) {
    try {
      $(tableEl).removeClass('dataTable');
    } catch {
      /* ignore */
    }
    return;
  }
  try {
    $(tableEl).DataTable().destroy(false);
    $(tableEl).removeClass('dataTable');
  } catch {
    try {
      $(tableEl).removeClass('dataTable');
    } catch {
      /* ignore */
    }
  }
}

